import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import lib from '../../../assets/utils/lib';
import AdminClaimsMonthsList from './AdminClaimsMonthsList';
import AdminMonthFacilitiesList from './AdminMonthFacilitiesList';
import AdminFacilityStatusSummary from './AdminFacilityStatusSummary';
import AdminFacilityClaimsList from './AdminFacilityClaimsList';
import BatchDetailView from '../Batches/BatchDetailView';
import BreadcrumbNav from '../../Shared/BreadcrumbNav';
import './AdminClaimsHierarchy.css';

/**
 * AdminClaimsHierarchy - Main container for admin hierarchical claims view
 * Simplified 3-level structure: Months → Facilities → Claims
 */
const AdminClaimsHierarchy = () => {
  const location = useLocation();
  const state = location.state;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const ADMIN_MONTHS_PARAMS = ['adminMonthsSearch', 'adminMonthsPage', 'adminMonthsPageSize'];
  const ADMIN_FACILITIES_PARAMS = ['adminFacilitiesSearch', 'adminFacilitiesPage', 'adminFacilitiesPageSize'];
  const ADMIN_CLAIMS_PARAMS = ['adminClaimsSearch', 'adminClaimsPage', 'adminClaimsPageSize'];
  const ADMIN_SCOPED_PARAMS = [
    ...ADMIN_MONTHS_PARAMS,
    ...ADMIN_FACILITIES_PARAMS,
    ...ADMIN_CLAIMS_PARAMS,
  ];
  
  // Helper to get month key from various formats
  const getMonthKey = (month) => {
    if (!month) return null;
    if (typeof month === 'string') return month;
    return month.month || month.key || null;
  };
  
  // Helper to get facility ID from various formats
  const getFacilityId = (facility) => {
    if (!facility) return null;
    if (typeof facility === 'object') return facility.facility_id || facility.id || null;
    return facility;
  };

  const pruneScopedParams = (params, targetLevel) => {
    const allowedByLevel = {
      months: ADMIN_MONTHS_PARAMS,
      facilities: ADMIN_FACILITIES_PARAMS,
      claims: [],
      claims_list: ADMIN_CLAIMS_PARAMS,
      claim_detail: ADMIN_CLAIMS_PARAMS,
    };
    const allowed = new Set(allowedByLevel[targetLevel] || []);

    ADMIN_SCOPED_PARAMS.forEach((key) => {
      if (!allowed.has(key)) {
        params.delete(key);
      }
    });

    // Clean up legacy shared keys so old links cannot leak state.
    params.delete('search');
    params.delete('page');
    params.delete('pageSize');
  };
  
  // Initialize from URL params first, then fallback to state
  const [level, setLevel] = useState(() => {
    const monthParam = searchParams.get('month');
    const facilityParam = searchParams.get('facility');
    const statusParam = searchParams.get('status');
    
    if (monthParam && facilityParam && statusParam !== null) {
      return 'claims_list';
    } else if (monthParam && facilityParam) {
      return 'claims';
    } else if (monthParam) {
      return 'facilities';
    } else if (state?.month && state?.facility) {
      return 'claims';
    } else if (state?.month) {
      return 'facilities';
    }
    return 'months';
  });
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      // Month is stored as string key in URL (e.g., "2024-12")
      // We'll fetch the full month object in useEffect if needed
      return monthParam;
    }
    return state?.month || null;
  });
  
  const [selectedFacility, setSelectedFacility] = useState(() => {
    const facilityParam = searchParams.get('facility');
    if (facilityParam) {
      // Facility ID from URL - will be fetched in useEffect
      return parseInt(facilityParam, 10);
    }
    // If facility is an object, use it directly
    if (state?.facility && typeof state.facility === 'object') {
      return state.facility;
    }
    return state?.facility || null;
  });
  
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const statusParam = searchParams.get('status');
    if (statusParam !== null) {
      return parseInt(statusParam, 10);
    }
    return null;
  });
  
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  
  // Update URL when navigation state changes
  useEffect(() => {
    const monthKey = getMonthKey(selectedMonth);
    const facilityId = getFacilityId(selectedFacility);
    
    // Start with existing params to preserve search and other params
    const params = new URLSearchParams(searchParams);
    
    // Always store just the month key (string) in URL for simplicity
    if (monthKey) {
      params.set('month', monthKey);
    } else {
      params.delete('month');
    }
    
    if (facilityId) {
      params.set('facility', facilityId.toString());
    } else {
      params.delete('facility');
    }
    
    if (selectedStatus !== null) {
      params.set('status', selectedStatus.toString());
    } else {
      params.delete('status');
    }
    
    pruneScopedParams(params, level);

    // Only update URL if params have changed (avoid infinite loop)
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedMonth, selectedFacility, selectedStatus, level, setSearchParams, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Effect to restore facility object when we have month and facility_id
  useEffect(() => {
    // If we have month and facility is a number (facility_id), fetch the facility object
    if (selectedMonth && selectedFacility && typeof selectedFacility === 'number') {
      const fetchFacility = async () => {
        try {
          const monthKey = getMonthKey(selectedMonth);
          const response = await lib.getAdminMonthFacilities(monthKey, { page: 1, pageSize: 100 });
          
          if (response?.data?.error === 0) {
            const facilities = response.data.data?.facilities || [];
            const facility = facilities.find(f => f.facility_id === selectedFacility);
            if (facility) {
              setSelectedFacility(facility);
            }
          }
        } catch (error) {
          console.error('Error fetching facility:', error);
        }
      };
      
      fetchFacility();
    }
  }, [selectedMonth, selectedFacility]);

  // Get status label helper
  const getStatusLabel = (statusCode) => {
    const statusLabels = {
      0: 'Pending CO',
      1: 'Pending HM',
      2: 'Pending CFO',
      3: 'Paid',
      4: 'Denied'
    };
    return statusLabels[statusCode] || `Status ${statusCode}`;
  };

  // Update breadcrumbs when navigation changes
  useEffect(() => {
    const crumbs = [
      { label: 'Claims', level: 'months', onClick: () => navigateToLevel('months') },
    ];

    if (selectedMonth) {
      crumbs.push({
        label: formatMonthLabel(selectedMonth),
        level: 'facilities',
        onClick: () => navigateToLevel('facilities'),
      });
    }

    if (selectedFacility) {
      crumbs.push({
        label: selectedFacility.facility_name || selectedFacility.name || 'Facility',
        level: 'claims',
        onClick: () => navigateToLevel('claims'),
      });
    }

    if (selectedStatus !== null) {
      crumbs.push({
        label: getStatusLabel(selectedStatus),
        level: 'claims_list',
        onClick: () => navigateToLevel('claims_list'),
      });
    }

    if (selectedClaim) {
      crumbs.push({
        label: selectedClaim.checkin_id || selectedClaim.claims_id || 'Claim',
        level: 'claim_detail',
      });
    }

    setBreadcrumbs(crumbs);
  }, [selectedMonth, selectedFacility, selectedStatus, selectedClaim, level]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatMonthLabel = (month) => {
    // Handle both string and object formats
    if (typeof month === 'string') {
      const date = new Date(month + '-01');
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    // Object format: { month: "2024-01", month_label: "December 2024", ... }
    if (month.month_label) return month.month_label;
    if (month.month || month.key) {
      const monthKey = month.month || month.key;
      const date = new Date(monthKey + '-01');
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return 'Unknown Month';
  };

  const navigateToLevel = (targetLevel) => {
    setLevel(targetLevel);
    
    // Start with existing params to preserve search
    const currentParams = new URLSearchParams(searchParams);
    
    // Clear selections when navigating up
    if (targetLevel === 'months') {
      setSelectedMonth(null);
      setSelectedFacility(null);
      setSelectedStatus(null);
      setSelectedClaim(null);
      // Clear navigation params but preserve search
      currentParams.delete('status');
      currentParams.delete('month');
      currentParams.delete('facility');
      pruneScopedParams(currentParams, targetLevel);
      setSearchParams(currentParams, { replace: true });
    } else if (targetLevel === 'facilities') {
      setSelectedFacility(null);
      setSelectedStatus(null);
      setSelectedClaim(null);
      // Keep month in URL, preserve search
      const monthKey = getMonthKey(selectedMonth);
      if (monthKey) {
        currentParams.set('month', monthKey);
      } else {
        currentParams.delete('month');
      }
      currentParams.delete('facility');
      currentParams.delete('status');
      pruneScopedParams(currentParams, targetLevel);
      setSearchParams(currentParams, { replace: true });
    } else if (targetLevel === 'claims') {
      setSelectedStatus(null);
      setSelectedClaim(null);
      // Keep month and facility in URL, preserve search
      const monthKey = getMonthKey(selectedMonth);
      const facilityId = getFacilityId(selectedFacility);
      if (monthKey) {
        currentParams.set('month', monthKey);
      } else {
        currentParams.delete('month');
      }
      if (facilityId) {
        currentParams.set('facility', facilityId.toString());
      } else {
        currentParams.delete('facility');
      }
      currentParams.delete('status');
      pruneScopedParams(currentParams, targetLevel);
      setSearchParams(currentParams, { replace: true });
    } else if (targetLevel === 'claims_list') {
      setSelectedClaim(null);
      // Keep month, facility, and status in URL, preserve search
      const monthKey = getMonthKey(selectedMonth);
      const facilityId = getFacilityId(selectedFacility);
      if (monthKey) {
        currentParams.set('month', monthKey);
      } else {
        currentParams.delete('month');
      }
      if (facilityId) {
        currentParams.set('facility', facilityId.toString());
      } else {
        currentParams.delete('facility');
      }
      if (selectedStatus !== null) {
        currentParams.set('status', selectedStatus.toString());
      } else {
        currentParams.delete('status');
      }
      pruneScopedParams(currentParams, targetLevel);
      setSearchParams(currentParams, { replace: true });
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setLevel('facilities');
  };

  const handleFacilitySelect = (facility) => {
    setSelectedFacility(facility);
    setLevel('claims');
  };

  const handleStatusSelect = (statusCode) => {
    setSelectedStatus(statusCode);
    setLevel('claims_list');
  };

  const handleClaimClick = (claim) => {
    setSelectedClaim(claim);
    setLevel('claim_detail');
  };

  const renderLevel = () => {
    switch (level) {
      case 'months':
        return (
          <AdminClaimsMonthsList
            onSelect={handleMonthSelect}
          />
        );
      case 'facilities':
        return (
          <AdminMonthFacilitiesList
            key={selectedMonth?.month || selectedMonth?.key || selectedMonth || 'facilities'}
            month={selectedMonth}
            onSelect={handleFacilitySelect}
          />
        );
      case 'claims':
        return (
          <AdminFacilityStatusSummary
            month={selectedMonth}
            facilityId={selectedFacility}
            onStatusClick={handleStatusSelect}
            onClaimClick={handleClaimClick}
          />
        );
      case 'claims_list':
        return (
          <AdminFacilityClaimsList
            month={selectedMonth}
            facilityId={selectedFacility}
            statusCode={selectedStatus}
            onClaimClick={handleClaimClick}
          />
        );
      case 'claim_detail':
        return (
          <BatchDetailView
            facility={selectedFacility}
            batch={selectedClaim}
            encounterDate={selectedClaim?.encounter_date}
            onBack={() => navigateToLevel('claims')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-claims-hierarchy">
      <BreadcrumbNav breadcrumbs={breadcrumbs} />
      <div className="admin-claims-hierarchy__content">
        {renderLevel()}
      </div>
    </div>
  );
};

export default AdminClaimsHierarchy;

